import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isTauri } from "./web-runtime";
import { loadReminder, saveReminder, syncReminder, type ReminderSetting } from "./reminders";
import { invoke, listen, platform } from "./tauri-web";
import { saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import {
  store, computeStreak, streakCopy, computeWeeklyTrend, trendSummaryCopy,
  emojiForScore, labelForScore, colorForScore, DEFAULT_TAGS, normalizeTags, normalizeTag,
  todayLocal, formatLocalDate, type MoodEntry, type WeeklyTrend, type MoodSignals,
} from "./store";
import { reflectWeek, askMoods, openWebLogin, BrainError, type BrainErrorCode } from "./brain";
import { buildExport } from "./export";
import { BackupMenu } from "./BackupMenu";
import { OrioraBadge } from "./OrioraBadge";
import Chat from "./chat/Chat";
import moodMarksUrl from "./assets/mood-marks.svg";
import emptyFirstCheckin from "./assets/empty-state-first-checkin.svg";
import { Sparkles, BarChart3, Trash2, X, Lock, Sprout, WifiOff, RefreshCw } from "lucide-react";

const fmtDate = (s: string) =>
  s ? new Date(s.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }) : "";
const WEEKDAY = (s: string) => new Date(s + "T00:00:00Z").toLocaleDateString("en-US", { weekday: "narrow", timeZone: "UTC" });
const LIST_CAP = 50000;

// "Oat & Cobalt" — the five drawn mood marks (mood-marks.svg = 5 tiles of 96px on a 480x96 sheet).
// The FORM carries the value (teardrop → deflated oval → circle → rising arch → radiant bloom),
// so mood never reads by color alone.
function MoodMark({ score, size = 30, title }: { score: number; size?: number; title?: string }) {
  const s = Math.min(5, Math.max(1, score));
  return (
    <span title={title} aria-label={title} role={title ? "img" : undefined} style={{
      display: "inline-block", width: size, height: size, flexShrink: 0,
      backgroundImage: `url(${moodMarksUrl})`, backgroundRepeat: "no-repeat",
      backgroundSize: `${size * 5}px ${size}px`, backgroundPosition: `${-(s - 1) * size}px 0`,
    }} />
  );
}

// R3 — the streak is one quiet worded line, never a card ("Logged eight days running").
const NUM_WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];
const spellNum = (n: number) => (n >= 0 && n < NUM_WORDS.length ? NUM_WORDS[n] : String(n));

// SCREENSHOT=0 — sim-only auth/consent bypass for identity screenshots. MUST stay 0 in committed/shipped code.
const SCREENSHOT = 0;

/**
 * a fortnight of logged moods — the shape a run of them makes, not a smiley.
 * Replaces the lucide `Sparkles` that 20 of the fleet's 26 chat launchers rendered in common;
 * the button outline changes with it, because one glyph in two colours is still one control
 * (Guideline 4.3(a)).
 */
function SwellMark({ size = 19 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M3.5 14.5c2.6 0 2.6-5 5.2-5s2.6 5 5.2 5 2.6-5 5.2-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2/§3, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out. (mirrors expense_audit_ai)
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground. THIS is the reliable catch for
  // "deleted my account, came back, still signed in": returning to the app (closing the
  // in-app delete window, or re-opening from the app switcher) re-checks the token, the
  // server rejects the deleted account, and we drop to login.
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns mood-tracker://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // mood-tracker://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      // Clear the on-device session WITHOUT a server /logout (the account is gone → global
      // signOut errors). Then FORCE the logged-out UI directly — onAuthStateChange/SIGNED_OUT
      // is unreliable with the async Tauri file storage, so don't depend on it.
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented && !SCREENSHOT) {
    // The LOCKED AI-consent notice (2026-07-07) — one notice, every app, only the app name varies.
    // No per-app dataNote slot; AiConsent renders the locked copy verbatim.
    return <AiConsent brand="Oriora Mood"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted && !SCREENSHOT) return <div style={{ height: "100%", display: "grid", placeItems: "center" }} className="muted">Loading…</div>;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Home email={SCREENSHOT ? "you@oriora.app" : email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card" style={{ width: "100%", maxWidth: 380, padding: "2rem", textAlign: "center", margin: "auto" }}>
        <div style={{ fontSize: "2.4rem", marginBottom: "0.4rem" }}>🙂</div>
        <div style={{ color: "var(--accent)", fontWeight: 700, fontSize: "var(--fs-sm)" }}>Oriora Mood</div>
        <h1 className="serif" style={{ fontSize: "1.5rem", margin: "0.4rem 0 0.4rem" }}>Sign in with Oriora</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, marginBottom: "1.25rem" }}>
          Uses your Oriora account — sign in and you're returned here. Your moods live on this device and Oriora doesn't retain them; the AI features run through your Oriora account. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try {
              const p = platform();
              if (p === "ios") await signInIos("mood-tracker");
              else if (p === "macos") await signInMac("mood-tracker");
              else if (p === "web") window.location.href = "/login?next=/mood-tracker/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
      </div>
    </main>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [entries, setEntries] = useState<MoodEntry[]>([]);
  const [editing, setEditing] = useState<MoodEntry | null>(null);
  const [showLog, setShowLog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<MoodEntry | null>(null);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — does not change
  // any existing screen. onChanged → reload() refreshes the entry list after a chat-driven mutation.
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const reflectRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<(() => void) | null>(null);

  const reload = useCallback(async () => {
    setEntries(await store.listEntries({ limit: LIST_CAP }));
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  // Native-menu actions (Actions menu in the macOS menu bar): Log / Reflect / Export.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "log": setEditing(null); setShowLog(true); break;
        case "reflect":
          reflectRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          reflectRef.current?.querySelector<HTMLButtonElement>("button")?.click();
          break;
        case "export": exportRef.current?.(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Tag correlations are DETERMINISTIC arithmetic computed on-device (signals.rs / web-signals.ts),
  // and until now they only ever ran inside the billed reflection — so the one thing a mood diary
  // is kept to find out was invisible unless you paid for a narration. The maths is free; the
  // narration is what is worth charging for. See DriversBoard.
  const [homeSignals, setHomeSignals] = useState<MoodSignals | null>(null);
  useEffect(() => {
    if (entries.length === 0) { setHomeSignals(null); return; }
    let alive = true;
    void (async () => {
      try {
        const sig = await store.computeSignals(todayLocal());
        if (alive) setHomeSignals(sig);
      } catch { /* the board simply does not render; the log is unaffected */ }
    })();
    return () => { alive = false; };
  }, [entries]);

  const dates = useMemo(() => entries.map((e) => e.logged_on), [entries]);
  const streak = useMemo(() => computeStreak(dates), [dates]);
  const trend = useMemo(() => computeWeeklyTrend(entries.map((e) => ({ logged_on: e.logged_on, score: e.score }))), [entries]);

  const doExport = useCallback(() => {
    if (entries.length === 0) return;
    const { csv, filename } = buildExport(entries);
    void (async () => { const p = await saveFileDialog({ defaultPath: filename }); if (p) await writeTextFile(p, csv); })();
  }, [entries]);
  exportRef.current = doExport;

  function startLog(entry?: MoodEntry) { setEditing(entry ?? null); setShowLog(true); }
  async function doDelete(id: string) {
    setConfirmDelete(null);
    await store.deleteEntry(id);
    await reload();
  }

  return (
    <main style={{
      maxWidth: 720, margin: "0 auto",
      // Safe-area aware so content clears the iPhone notch/home-indicator (viewport-fit=cover).
      padding: "max(2rem, env(safe-area-inset-top)) max(1.5rem, env(safe-area-inset-right)) calc(2rem + env(safe-area-inset-bottom)) max(1.5rem, env(safe-area-inset-left))",
    }}>
      {offline && (
        <div role="status" className="row" style={{ gap: "0.45rem", fontSize: "var(--fs-xs)", color: "var(--warning)", background: "rgba(217,147,58,0.12)", border: "1px solid rgba(217,147,58,0.35)", borderRadius: 8, padding: "0.45rem 0.75rem", marginBottom: "1rem" }}>
          <WifiOff size={14} style={{ flexShrink: 0 }} /> AI reflection paused — your moods still save locally
        </div>
      )}

      <header className="row" style={{ justifyContent: "space-between", marginBottom: "1.25rem" }}>
        <div className="row" style={{ gap: "0.55rem" }}>
          <MoodMark score={5} size={22} />
          <h1 className="serif" style={{ fontSize: "1.5rem", margin: 0 }}>Mood</h1>
        </div>
        <div className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
          <BackupMenu
            appName="Mood" fileSlug="mood" appScheme="mood-tracker"
            hasData={entries.length > 0} onRestored={reload}
            onExportCsv={entries.length > 0 ? doExport : undefined}
          />
          <AccountMenu email={email} appSlug="mood-tracker" appName="Oriora Mood" onSignedOut={onSignedOut} />
          <AddFundsMenu />
        </div>
      </header>

      {/* R1 — the check-in IS the opening surface. */}
      <CheckIn entries={entries} onSaved={reload} onEditToday={(e) => startLog(e)} />

      <DailyNudge />

      {/* R3 — the streak, demoted to one quiet worded line. */}
      {streak > 0 && (
        <div className="streak-line">
          Logged {spellNum(streak)} day{streak === 1 ? "" : "s"} running · {entries.length} entr{entries.length === 1 ? "y" : "ies"} in total
        </div>
      )}

      <div className="row" style={{ justifyContent: "space-between", margin: "1.25rem 0 0.6rem" }}>
        <h2 className="serif" style={{ fontSize: "1.15rem", margin: 0 }}>Recent entries</h2>
      </div>

      {entries.length === 0 ? (
        <EmptyLog />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.45rem" }}>
          {entries.slice(0, 30).map((e) => (
            <EntryTile key={e.id} e={e} onEdit={() => startLog(e)} onDelete={() => setConfirmDelete(e)} />
          ))}
        </div>
      )}

      {entries.length > 0 && (
        <div className="card" style={{ padding: "1.1rem 1.25rem", margin: "1rem 0" }}>
          <div className="row" style={{ marginBottom: "0.6rem", gap: "0.4rem" }}>
            <BarChart3 size={16} style={{ color: "var(--accent)" }} />
            <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>This week</div>
          </div>
          <TrendChart trend={trend} />
          <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: "0.6rem", color: "var(--ink-2)" }}>{trendSummaryCopy(trend)}</div>
        </div>
      )}

      {entries.length > 0 && <MonthPixels entries={entries} />}

      {/* R2 — the deltas panel, demoted below the log. */}
      <DriversBoard signals={homeSignals} />

      <div ref={reflectRef}>
        <WeeklyReflection entries={entries} />
        <AskMoods entries={entries} />
      </div>

      {showLog && (
        <LogEntry editing={editing} onClose={() => { setShowLog(false); setEditing(null); }} onSaved={() => { setShowLog(false); setEditing(null); void reload(); }} />
      )}

      {confirmDelete && (
        <ModalShell title="Delete entry?" onCancel={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0 0 1rem" }}>
            Delete your {fmtDate(confirmDelete.logged_on)} entry? This can’t be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.4rem" }}>
            <button className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn btn-primary" style={{ fontSize: "var(--fs-xs)", background: "var(--rose)", borderColor: "var(--rose)" }} onClick={() => doDelete(confirmDelete.id)}>Delete</button>
          </div>
        </ModalShell>
      )}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          + slide-over live on top; no existing screen is altered. A chat-driven change reloads the list. */}
      {!chatOpen && (
        <button className="omc-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
          <SwellMark size={19} />
        </button>
      )}
      <aside className={`omc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="omc-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to log, find, or reflect</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
    </main>
  );
}

/**
 * "What moves it" — ledger row 2b.
 *
 * The home led with a STREAK counter and a reverse-chronological list of entries. Both answer "did
 * I log?", which is a question about the app's own usage. The question someone actually keeps a
 * mood diary to answer is "what makes my days better or worse?" — and the app already computed it:
 * `TagCorrelation { tag, count, avg_with_tag, delta }`, ranked by |delta|, in signals.rs and its
 * faithful web port.
 *
 * It was only ever computed INSIDE the billed weekly reflection, so the single most useful thing
 * the diary knows was invisible unless you paid for a narration. That is the wrong split. The
 * arithmetic is deterministic and runs on-device for nothing; what is worth charging for is the
 * interpretation — which is exactly what the reflection still does.
 *
 * ⚠️ OBSERVATION, NEVER PRESCRIPTION. This app's fence forbids telling anyone what to do about
 * their mood. "Days tagged sleep average 1.2 higher" is a fact about their own log. "Get more
 * sleep" would be advice, and health advice at that. The copy here states differences and nothing
 * else — no arrows implying cause, no suggestions, and a plain note that these are associations.
 */
function DriversBoard({ signals }: { signals: MoodSignals | null }) {
  const rows = signals?.tag_correlations ?? [];
  // `direction: "same"` means the difference is inside ±0.1 — not worth showing as a difference.
  const meaningful = rows.filter((t) => t.direction !== "same");
  if (meaningful.length < 2) return null;

  const higher = meaningful.filter((t) => t.delta > 0).slice(0, 3);
  const lower = meaningful.filter((t) => t.delta < 0).slice(0, 3);
  const fmt = (d: number) => `${d > 0 ? "+" : "−"}${Math.abs(d).toFixed(1)}`;

  const Row = ({ t }: { t: MoodSignals["tag_correlations"][number] }) => (
    <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline", gap: "0.6rem", borderTop: "1px solid var(--line)", padding: "0.5rem 0" }}>
      <span style={{ minWidth: 0 }}>
        <span style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{t.tag}</span>
        <span className="muted" style={{ fontSize: "var(--fs-xxs)", display: "block" }}>
          {t.count} {t.count === 1 ? "day" : "days"} · averages {t.avg_with_tag.toFixed(1)}
        </span>
      </span>
      <span className="tnum" style={{ flexShrink: 0, fontWeight: 700, fontSize: "var(--fs-md)", color: t.delta > 0 ? "var(--accent)" : "var(--ink-2)" }}>
        {fmt(t.delta)}
      </span>
    </div>
  );

  return (
    <div className="card fade-up" style={{ padding: "1rem 1.15rem", marginBottom: "1rem" }}>
      <div className="row" style={{ gap: "0.4rem", marginBottom: "0.15rem" }}>
        <BarChart3 size={15} style={{ color: "var(--accent)" }} />
        <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>What your days differ by</div>
      </div>
      <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginBottom: "0.35rem" }}>
        Compared with your own average of {(rows[0]?.overall_avg ?? 0).toFixed(1)} over the last 8 weeks
      </div>

      {higher.length > 0 && (
        <>
          <div className="eyebrow" style={{ marginTop: "0.5rem", marginBottom: 0 }}>Days that run higher</div>
          {higher.map((t) => <Row key={t.tag} t={t} />)}
        </>
      )}
      {lower.length > 0 && (
        <>
          <div className="eyebrow" style={{ marginTop: "0.75rem", marginBottom: 0 }}>Days that run lower</div>
          {lower.map((t) => <Row key={t.tag} t={t} />)}
        </>
      )}

      {/* Associations, not causes — said plainly rather than left for the reader to assume. */}
      <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: "0.7rem" }}>
        These are differences in your own log, not causes.
      </div>
    </div>
  );
}

/**
 * The daily reminder. Every competitor ships one; we had none.
 *
 * Deliberately worded as an open door — see reminders.ts. No streak language anywhere near it:
 * "don't break your run" is pressure, and pressure is the wrong thing to apply to someone logging
 * how they feel.
 */
function DailyNudge() {
  const [setting, setSetting] = useState<ReminderSetting>(loadReminder);
  const [msg, setMsg] = useState<string | null>(null);

  // Re-lay the schedule each launch so the fortnight ahead stays filled.
  useEffect(() => { if (setting.enabled) void syncReminder(setting); /* eslint-disable-next-line */ }, []);

  if (!isTauri()) return null; // no notification plugin in the browser — never offer a dead switch

  const apply = async (next: ReminderSetting) => {
    setSetting(next);
    saveReminder(next);
    const on = await syncReminder(next);
    setMsg(
      !next.enabled ? "Reminder off."
      : on ? `We'll nudge you at ${next.time}, on this device.`
      : "Notifications are off for this app — turn them on in Settings to be reminded.",
    );
  };

  return (
    <div className="row" style={{ gap: "0.45rem", alignItems: "center", flexWrap: "wrap", marginTop: "0.9rem" }}>
      <label className="muted" style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: "var(--fs-sm)" }}>
        <input
          type="checkbox"
          checked={setting.enabled}
          onChange={(ev) => void apply({ ...setting, enabled: ev.target.checked })}
          aria-label="Remind me to log how I'm doing"
        />
        Remind me to check in
      </label>
      {setting.enabled && (
        <input type="time" value={setting.time} aria-label="Reminder time"
          onChange={(ev) => void apply({ ...setting, time: ev.target.value })} />
      )}
      {msg && <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>{msg}</span>}
    </div>
  );
}

// R1 — the check-in surface: the opening screen. Today unlogged → five marks ready to tap
// (tap = select + expand tags/note); today logged → a compact summary with Edit.
function CheckIn({ entries, onSaved, onEditToday }: { entries: MoodEntry[]; onSaved: () => void; onEditToday: (e: MoodEntry) => void }) {
  // Every entry logged today, newest first — a mood is not a property of a day, so there may be
  // several. (Daylio and DailyBean, the two 60k+ apps in this category, both work this way.)
  const todaysAll = entries.filter((e) => e.logged_on === todayLocal());
  const todays = todaysAll[0] ?? null;
  const [score, setScore] = useState<number | null>(null);
  // WHEN this entry is for. Defaults to now, so the common case stays a single tap; opening it is
  // what lets someone fix a day they missed.
  const [whenOpen, setWhenOpen] = useState(false);
  const [onDate, setOnDate] = useState(todayLocal());
  const [atTime, setAtTime] = useState(() => new Date().toTimeString().slice(0, 5));
  const [note, setNote] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [customTag, setCustomTag] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dateLine = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  function toggleTag(t: string) {
    const n = normalizeTag(t);
    setTags((cur) => (cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n]));
  }
  function addCustom() {
    const n = normalizeTag(customTag);
    if (n && !tags.includes(n)) setTags((c) => [...c, n]);
    setCustomTag("");
  }
  async function save() {
    if (score === null) return;
    setSaving(true); setErr(null);
    try {
      await store.createEntry({ score, logged_on: onDate, logged_at: atTime, note: note.trim(), tags: normalizeTags(tags) });
      setScore(null); setNote(""); setTags([]); setWhenOpen(false);
      setOnDate(todayLocal()); setAtTime(new Date().toTimeString().slice(0, 5));
      setSaving(false);
      onSaved();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(`Could not save — try again. (${msg})`);
      setSaving(false);
    }
  }

  return (
    <div className="checkin">
      {/* What is already down for today — shown, never a lock. The old build returned here and
          made the day read-only, so an evening that went differently could not be recorded. */}
      {todaysAll.length > 0 && (
        <div style={{ marginBottom: "0.9rem" }}>
          <div className="checkin-eyebrow">Today · {dateLine}</div>
          {todaysAll.map((e) => (
            <div key={e.id} className="today-logged" style={{ marginTop: 8 }}>
              <span className="today-logged-mark"><MoodMark score={e.score} size={34} title={labelForScore(e.score)} /></span>
              <div className="today-logged-body">
                <div className="today-logged-label">{e.logged_at ? `${e.logged_at} · ` : ""}{labelForScore(e.score)}</div>
                {e.tags.length > 0 && <div className="today-logged-meta">{e.tags.join(" · ")}</div>}
                {e.note && <div className="today-logged-note">{e.note}</div>}
              </div>
              <button className="today-logged-edit" onClick={() => onEditToday(e)}>Edit</button>
            </div>
          ))}
        </div>
      )}

      <div className="checkin-head">
        <div className="checkin-eyebrow">{todaysAll.length > 0 ? "Another entry" : `Today · ${dateLine}`}</div>
        <div className="checkin-prompt">{todaysAll.length > 0 ? "How are you now?" : "How’s today?"}</div>
      </div>
      <div className="mood-picker">
        {[1, 2, 3, 4, 5].map((s) => (
          <button key={s} type="button" className={`mood-mark-btn${score === s ? " selected" : ""}`}
            onClick={() => setScore(s)} aria-pressed={score === s} aria-label={labelForScore(s)}>
            <MoodMark score={s} size={34} />
            <span className="mood-mark-lbl">{labelForScore(s)}</span>
          </button>
        ))}
      </div>
      {score !== null && (
        <div className="checkin-expand">
          {/* WHEN — collapsed by default so the common case is still one tap. Opening it is what
              makes a day you missed reachable at all; the Rust side always accepted a date, the
              UI just never sent one. */}
          <div className="field-label-row">
            <span className="field-label">When</span>
            <button type="button" className="btn btn-ghost" style={{ fontSize: "var(--fs-xs)", padding: "0.1rem 0.35rem" }}
              onClick={() => setWhenOpen((o) => !o)}>
              {whenOpen ? "now" : (onDate === todayLocal() ? "now · change" : `${onDate} ${atTime} · change`)}
            </button>
          </div>
          {whenOpen && (
            <div className="row" style={{ gap: "0.4rem", marginBottom: "0.75rem" }}>
              <input type="date" value={onDate} max={todayLocal()} onChange={(e) => setOnDate(e.target.value)}
                style={{ fontSize: "var(--fs-xs)", flex: 1, width: "auto" }} aria-label="Date" />
              <input type="time" value={atTime} onChange={(e) => setAtTime(e.target.value)}
                style={{ fontSize: "var(--fs-xs)", width: "auto" }} aria-label="Time" />
            </div>
          )}
          <div className="field-label-row"><span className="field-label">Tags <span className="field-label-opt">(optional)</span></span></div>
          <div className="tag-wrap">
            {DEFAULT_TAGS.map((t) => (
              <button key={t} type="button" className={`chip${tags.includes(normalizeTag(t)) ? " selected" : ""}`} onClick={() => toggleTag(t)}>{t}</button>
            ))}
            {tags.filter((t) => !DEFAULT_TAGS.map(normalizeTag).includes(t)).map((t) => (
              <button key={t} type="button" className="chip selected" onClick={() => toggleTag(t)}>{t} <X size={11} /></button>
            ))}
          </div>
          <form className="row" style={{ gap: "0.4rem", marginBottom: "0.75rem" }} onSubmit={(e) => { e.preventDefault(); addCustom(); }}>
            <input placeholder="Add your own tag…" value={customTag} onChange={(e) => setCustomTag(e.target.value)} style={{ fontSize: "var(--fs-xs)", flex: 1, width: "auto" }} />
            <button type="submit" className="btn" style={{ fontSize: "var(--fs-xs)" }} disabled={!customTag.trim()}>Add</button>
          </form>
          <div className="field-label-row"><span className="field-label">Note <span className="field-label-opt">(optional)</span></span></div>
          <textarea className="note-input" rows={2} placeholder="What happened today?" value={note} onChange={(e) => setNote(e.target.value)} style={{ marginBottom: "0.75rem" }} />
          {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.6rem" }}>{err}</div>}
          <div className="checkin-actions">
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save entry"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── READY-STATE (2026-09-19) — what the log IS, and what you can do with it ──────
// The check-in above is already the opening surface; what a first-time reader could not see is
// what the record becomes — that it stays theirs, that it comes out whole, and that it can be
// worked by typing. Those are PARITY-MATRIX rows (export free · backup to the owner's own cloud ·
// an assistant that can touch the log at all — no leader in the matrix has one).
//
// ⚠️ Health-adjacent: every line here describes what the app RECORDS and what the OWNER can DO.
// Nothing claims a benefit, promises an interpretation, or says anything about how the person is.
function EmptyLog() {
  return (
    <div className="empty-state">
      <img src={emptyFirstCheckin} alt="" className="empty-state-illus" />
      <div className="empty-state-title">Nothing logged yet</div>
      <div className="empty-state-body">
        Mark today above and this becomes your log — a private, one-a-day record of how things went.
      </div>
      {/* Ordered by where this app actually differs. The leader in this category already keeps
          entries on the device and already exports — those two are table stakes and are stated
          here as plain facts about ours, not as contrasts. The assistant is the one the leader
          has no equivalent of, so it leads. */}
      <ul className="mt-facts">
        <li>
          <b>You can say it in words.</b> The assistant can <i>log</i> a day, <i>filter</i> what you
          have logged, or <i>export</i> it — if typing suits you better than tapping.
        </li>
        <li>
          <b>It stays yours.</b> The entries are written to this device. A copy goes to your own
          GitHub or Drive when you back it up — there is no account of ours holding them.
        </li>
        <li>
          <b>It comes out whole.</b> Export every entry to CSV whenever you want, free, with the
          notes and tags you wrote.
        </li>
      </ul>
    </div>
  );
}

function MonthPixels({ entries }: { entries: MoodEntry[] }) {
  const today = new Date();
  const y = today.getFullYear(), m = today.getMonth();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const lead = (new Date(y, m, 1).getDay() + 6) % 7; // Monday-first offset
  const todayStr = todayLocal();
  // One square per day, and with several entries a day it must be the LATEST — `new Map(...)`
  // keeps the LAST of each key, and the list arrives newest-first, so the naive form silently
  // showed the OLDEST mood of the day.
  const byDate = useMemo(() => {
    const m = new Map<string, MoodEntry>();
    for (const e of entries) if (!m.has(e.logged_on)) m.set(e.logged_on, e);
    return m;
  }, [entries]);
  const monthName = today.toLocaleDateString("en-US", { month: "long" });
  const cells: (MoodEntry | null | undefined)[] = [];
  for (let i = 0; i < lead; i++) cells.push(undefined); // leading blanks
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${y.toString().padStart(4, "0")}-${(m + 1).toString().padStart(2, "0")}-${d.toString().padStart(2, "0")}`;
    cells.push(byDate.get(key) ?? null);
  }
  return (
    <div className="card" style={{ padding: "1.1rem 1.25rem", margin: "1rem 0" }}>
      <div className="row" style={{ marginBottom: "0.6rem", gap: "0.4rem" }}>
        <BarChart3 size={16} style={{ color: "var(--accent)" }} />
        <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{monthName} in pixels</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
          <div key={`h${i}`} className="muted" style={{ fontSize: 10, textAlign: "center" }}>{d}</div>
        ))}
        {cells.map((c, i) => {
          if (c === undefined) return <div key={i} />;
          const dayNum = i - lead + 1;
          const key = `${y.toString().padStart(4, "0")}-${(m + 1).toString().padStart(2, "0")}-${dayNum.toString().padStart(2, "0")}`;
          const isFuture = key > todayStr;
          return (
            <div key={i}
              title={c ? `${key} · ${labelForScore(c.score)}` : isFuture ? key : `${key} · not logged`}
              style={{
                aspectRatio: "1", borderRadius: 6,
                background: c ? colorForScore(c.score) : "var(--elev-2)",
                border: key === todayStr ? "2px solid var(--accent)" : "1px solid var(--border)",
                opacity: isFuture ? 0.35 : 1,
                display: "grid", placeItems: "center",
              }}>
              <span style={{ fontSize: 9, color: c ? "rgba(255,255,255,0.9)" : "var(--ink-3)" }}>{dayNum}</span>
            </div>
          );
        })}
      </div>
      <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: "0.6rem" }}>
        Each square is a day this month, colored by your logged mood.
      </div>
    </div>
  );
}

// "This week" reimagined (R4 grammar): the mark's FORM carries each day; empty days are quiet dots.
function TrendChart({ trend }: { trend: WeeklyTrend }) {
  return (
    <div className="week-rhythm">
      {trend.days.map((d) => {
        const has = d.averageScore !== null;
        return (
          <div className="week-rhythm-day" key={d.date} title={has ? `${labelForScore(Math.round(d.averageScore!))} · ${d.count} entr${d.count === 1 ? "y" : "ies"}` : "no entry"}>
            {has ? <MoodMark score={Math.round(d.averageScore!)} size={22} /> : <span className="week-rhythm-empty-dot" />}
            <span className="week-rhythm-letter">{WEEKDAY(d.date)}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── The ONE AI feature: weekly mood-pattern reflection (logging only, no advice) ──
// Renders the reflection beautifully, and gives every real-use path its own state:
// loading skeleton, success, empty result, insufficient-funds, and
// network/unavailable — each typed, not a raw line.
type ReflectErr = { code: BrainErrorCode | "empty"; message: string };

function AskMoods({ entries }: { entries: MoodEntry[] }) {
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ReflectErr | null>(null);

  const daysScoped = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 13);
    const cutoffStr = formatLocalDate(cutoff);
    return new Set(entries.filter((e) => e.logged_on >= cutoffStr).map((e) => e.logged_on)).size;
  }, [entries]);

  async function ask() {
    const question = q.trim();
    if (!question) return;
    setBusy(true); setErr(null); setAnswer(null);
    try {
      const sig = await store.computeSignals(todayLocal());
      const r = await askMoods(question, sig);
      const a = r.answer.trim();
      if (!a) setErr({ code: "empty", message: "Nothing in your log answers that yet — log a few more days." });
      else setAnswer(a);
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Couldn't answer — please try again." });
    } finally { setBusy(false); }
  }

  if (entries.length === 0) return null;
  const canAsk = daysScoped > 0 && q.trim().length > 0 && !busy;
  return (
    <div className="card" style={{ padding: "1rem 1.25rem", borderLeft: "3px solid var(--accent)" }}>
      <div className="row" style={{ fontWeight: 600, fontSize: "var(--fs-sm)", gap: "0.35rem" }}>
        <Sparkles size={14} style={{ color: "var(--accent)", flexShrink: 0 }} /> Ask your moods
      </div>
      <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 6 }}>
        Ask about your own log — e.g. “Which day am I usually lowest?” or “Does work go with worse moods?”
      </div>
      <div className="row" style={{ gap: "0.5rem", marginTop: 10 }}>
        <input
          placeholder="Ask about your moods…"
          value={q}
          maxLength={500}
          disabled={busy}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && canAsk) ask(); }}
          style={{ flex: 1, fontSize: "var(--fs-sm)" }}
        />
        <button className="btn btn-primary" style={{ fontSize: "var(--fs-xs)", padding: "0.35rem 0.7rem", flexShrink: 0 }} disabled={!canAsk} onClick={ask}>
          {busy ? "Asking…" : "Ask"}
        </button>
      </div>
      {!busy && daysScoped === 0 && (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 6 }}>Log a few moods this fortnight to ask.</div>
      )}
      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.75rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "85%" }} />
        </div>
      )}
      {err && !busy && <ReflectionError err={err} onRetry={ask} />}
      {answer && !busy && (
        <div style={{ marginTop: "0.6rem" }} aria-live="polite">
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.6, margin: 0 }}>{answer}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          <div className="row" style={{ marginTop: 10 }}>
            <span className="muted" style={{ fontSize: 10 }}>Describes your logged moods — not medical or mental-health advice.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function WeeklyReflection({ entries }: { entries: MoodEntry[] }) {
  const [text, setText] = useState<string | null>(null);
  const [signals, setSignals] = useState<MoodSignals | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ReflectErr | null>(null);

  // Gate the button instantly off the in-memory entries — the actual reflection
  // sends a DERIVED-SIGNALS object computed on-device (signals.rs), not the raw log.
  // Distinct days logged in the last 14 (the headline window) decides "enough to reflect".
  const daysScoped = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 13);
    const cutoffStr = formatLocalDate(cutoff);
    return new Set(entries.filter((e) => e.logged_on >= cutoffStr).map((e) => e.logged_on)).size;
  }, [entries]);

  async function run() {
    setBusy(true); setErr(null); setText(null); setSignals(null);
    try {
      // Compute the signals locally first (fast, on-device), then send only those.
      const sig = await store.computeSignals(todayLocal());
      const r = await reflectWeek(sig);
      const reflection = r.reflection.trim();
      if (!reflection) {
        setErr({ code: "empty", message: "Not enough recent moods to reflect on yet — log a few more days." });
      } else {
        setText(reflection);
        setSignals(sig);
      }
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Reflection failed — please try again." });
    } finally { setBusy(false); }
  }

  if (entries.length === 0) return null;
  const canReflect = daysScoped > 0;
  return (
    <div className="card" style={{ padding: "1rem 1.25rem", borderLeft: "3px solid var(--accent)" }}>
      <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem" }}>
        <div className="row" style={{ fontWeight: 600, fontSize: "var(--fs-sm)", gap: "0.35rem", flexWrap: "wrap" }}>
          <Sparkles size={14} style={{ color: "var(--accent)", flexShrink: 0 }} /> Weekly reflection
        </div>
        <button className="btn btn-primary row" style={{ fontSize: "var(--fs-xs)", padding: "0.35rem 0.7rem", gap: "0.35rem", flexShrink: 0 }} disabled={busy || !canReflect} onClick={run}>
          {busy ? "Reflecting…" : text ? (<><RefreshCw size={12} /> Refresh</>) : "Reflect on my week"}
        </button>
      </div>

      {/* Helper line under the header — guides the user to a usable state. */}
      {!busy && !text && !err && daysScoped > 0 && (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 6 }}>
          Finds the patterns across your moods — tag links, weekday rhythm, this fortnight vs last.
        </div>
      )}
      {!busy && !text && daysScoped === 0 && (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 6 }}>Log a few moods this fortnight to enable a reflection.</div>
      )}

      {/* Loading state — a calm shimmer, not just a button label flip. */}
      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.75rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Sparkles size={12} style={{ color: "var(--accent)" }} /> Finding the patterns across your moods…
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "92%" }} />
          <div className="skeleton-line" style={{ width: "70%" }} />
        </div>
      )}

      {/* Error states — each typed, with the right affordance. */}
      {err && !busy && <ReflectionError err={err} onRetry={run} />}

      {/* Success — the reflection + the evidence it drew from. */}
      {text && !busy && (
        <div style={{ marginTop: "0.6rem" }} aria-live="polite">
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.6, margin: 0 }}>{text}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          {signals && <SignalEvidence signals={signals} />}
          <div className="row" style={{ marginTop: 10 }}>
            <span className="muted" style={{ fontSize: 10 }}>Reflects your logged moods — not medical or mental-health advice.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ReflectionError({ err, onRetry }: { err: ReflectErr; onRetry: () => void }) {
  // Insufficient funds — point to the Oriora account on the web, no amounts shown.
  if (err.code === "insufficient_funds") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "rgba(217,147,58,0.1)", border: "1px solid rgba(217,147,58,0.3)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Sparkles size={15} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600 }}>Add funds to keep reflecting</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then reflect again.</div>
        </div>
      </div>
    );
  }
  // BYOK gate — Managed AI blocked for this app; needs a vendor key.
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "var(--elev-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Sparkles size={15} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  // Empty result — gentle, no retry button (more data is the fix, not a retry).
  if (err.code === "empty") {
    return <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 10 }}>{err.message}</div>;
  }
  // Network / session / unavailable — recoverable, offer a retry.
  const retryable = err.code === "unavailable" || err.code === "unauthenticated";
  return (
    <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ fontSize: "var(--fs-xs)", color: "var(--rose)" }}>{err.message}</span>
      {retryable && err.code === "unavailable" && (
        <button className="btn row" style={{ fontSize: "var(--fs-xs)", padding: "0.25rem 0.55rem", gap: "0.3rem", flexShrink: 0 }} onClick={onRetry}>
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

// The receipts: the actual derived numbers the reflection drew from. Purely
// observational — deltas, averages, std-dev change — so the user can see what the AI
// saw. No advice, no judgement; just the computed facts behind the sentences.
function SignalEvidence({ signals }: { signals: MoodSignals }) {
  const chips: { label: string; title: string }[] = [];

  const sh = signals.window_shift;
  if (sh) {
    const arrow = sh.direction === "up" ? "↑" : sh.direction === "down" ? "↓" : "→";
    const sign = sh.delta > 0 ? "+" : "";
    chips.push({
      label: `14d vs prior ${arrow} ${sign}${sh.delta.toFixed(2)}`,
      title: `Last 14 days averaged ${sh.current_avg.toFixed(2)} vs ${sh.prior_avg.toFixed(2)} the 14 days before`,
    });
  }

  // Most-distinctive tag link (already sorted by |delta| in signals.rs).
  const tag = signals.tag_correlations.find((t) => t.direction !== "same");
  if (tag) {
    const sign = tag.delta > 0 ? "+" : "";
    chips.push({
      label: `${tag.tag} ${sign}${tag.delta.toFixed(2)}`,
      title: `Days tagged "${tag.tag}" (${tag.count}×) averaged ${tag.avg_with_tag.toFixed(2)} vs your ${tag.overall_avg.toFixed(2)} overall`,
    });
  }

  // Weekday spread: highest vs lowest day with data.
  const dows = signals.day_of_week.filter((d) => d.avg_score !== null && d.count > 0);
  if (dows.length >= 2) {
    const hi = dows.reduce((a, b) => (b.avg_score! > a.avg_score! ? b : a));
    const lo = dows.reduce((a, b) => (b.avg_score! < a.avg_score! ? b : a));
    if (hi.weekday !== lo.weekday) {
      chips.push({
        label: `${hi.weekday} ${hi.avg_score!.toFixed(1)} · ${lo.weekday} ${lo.avg_score!.toFixed(1)}`,
        title: `Highest weekday avg ${hi.weekday} ${hi.avg_score!.toFixed(2)}, lowest ${lo.weekday} ${lo.avg_score!.toFixed(2)} (last 8 weeks)`,
      });
    }
  }

  // Volatility change (only when both windows had data).
  if (signals.volatility.change && signals.volatility.current_std_dev !== null) {
    chips.push({
      label: `swing ${signals.volatility.change}`,
      title: `Score spread (std-dev) ${signals.volatility.current_std_dev.toFixed(2)} now vs ${signals.volatility.prior_std_dev?.toFixed(2) ?? "—"} before`,
    });
  }

  // Multi-week trajectory (≥4 weeks of data).
  const tr = signals.trajectory;
  if (tr && tr.direction !== "flat") {
    chips.push({
      label: `${tr.weeks.length}-wk ${tr.direction}`,
      title: `${tr.weeks.length}-week trajectory ${tr.direction} (slope ${tr.slope_per_week >= 0 ? "+" : ""}${tr.slope_per_week.toFixed(2)}/wk)`,
    });
  }

  if (chips.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: "0.6rem" }}>
      {chips.map((c) => (
        <span key={c.label} className="chip tnum" title={c.title}
          style={{ padding: "0.1rem 0.45rem", fontSize: 10, background: "var(--accent-soft)", borderColor: "transparent", color: "var(--accent-soft-ink)" }}>
          {c.label}
        </span>
      ))}
    </div>
  );
}

function EntryTile({ e, onEdit, onDelete }: { e: MoodEntry; onEdit: () => void; onDelete: () => void }) {
  return (
    <div className="day-row" onClick={onEdit}>
      <span className="day-row-mark"><MoodMark score={e.score} size={30} title={labelForScore(e.score)} /></span>
      <div className="day-row-body">
        <div className="day-row-date">{fmtDate(e.logged_on)}</div>
        {e.note && <div className="day-row-note">{e.note}</div>}
        {e.tags.length > 0 && (
          <div className="day-row-tags">
            {e.tags.map((t) => <span key={t} className="chip" style={{ padding: "0.1rem 0.45rem", fontSize: 10 }}>{t}</span>)}
          </div>
        )}
      </div>
      <button className="btn" title="Delete entry" aria-label="Delete entry"
        style={{ padding: "0.3rem 0.4rem", border: "1px solid transparent", background: "none", color: "var(--ink-3)" }}
        onClick={(ev) => { ev.stopPropagation(); onDelete(); }}>
        <Trash2 size={15} />
      </button>
    </div>
  );
}

function LogEntry({ editing, onClose, onSaved }: { editing: MoodEntry | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [score, setScore] = useState<number | null>(editing?.score ?? null);
  const [note, setNote] = useState(editing?.note ?? "");
  const [tags, setTags] = useState<string[]>(editing ? [...editing.tags] : []);
  const [customTag, setCustomTag] = useState("");
  // WHEN this entry is for. A day can hold several entries, so add can target a missed past day
  // and edit can correct the day/time it was logged. Defaults: the entry's own when, or now.
  const [onDate, setOnDate] = useState(editing?.logged_on ?? todayLocal());
  const [atTime, setAtTime] = useState(editing?.logged_at || new Date().toTimeString().slice(0, 5));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggleTag(t: string) {
    const n = normalizeTag(t);
    setTags((cur) => (cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n]));
  }
  function addCustom() {
    const n = normalizeTag(customTag);
    if (n && !tags.includes(n)) setTags((c) => [...c, n]);
    setCustomTag("");
  }

  async function save() {
    if (score === null) return;
    setSaving(true); setErr(null);
    try {
      if (isEditing) {
        await store.updateEntry(editing!.id, { score, logged_on: onDate, logged_at: atTime, note: note.trim(), tags: normalizeTags(tags) });
      } else {
        await store.createEntry({ score, logged_on: onDate, logged_at: atTime, note: note.trim(), tags: normalizeTags(tags) });
      }
      onSaved();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(`Could not save — try again. (${msg})`);
      setSaving(false);
    }
  }

  return (
    <ModalShell title={isEditing ? "Edit entry" : "Log mood"} onCancel={onClose} wide>
      <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.6rem" }}>
        {isEditing || onDate !== todayLocal() ? "How were you?" : "How’s today?"}
      </div>
      <div className="mood-picker" style={{ marginBottom: "1.1rem", marginTop: 0 }}>
        {[1, 2, 3, 4, 5].map((s) => (
          <button key={s} type="button" className={`mood-mark-btn${score === s ? " selected" : ""}`} onClick={() => setScore(s)} aria-pressed={score === s} aria-label={labelForScore(s)}>
            <MoodMark score={s} size={34} />
            <span className="mood-mark-lbl">{labelForScore(s)}</span>
          </button>
        ))}
      </div>

      <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.5rem" }}>When</div>
      <div className="row" style={{ gap: "0.4rem", marginBottom: "1.1rem" }}>
        <input type="date" value={onDate} max={todayLocal()} onChange={(e) => setOnDate(e.target.value)}
          style={{ fontSize: "var(--fs-xs)", flex: 1, width: "auto" }} aria-label="Date" />
        <input type="time" value={atTime} onChange={(e) => setAtTime(e.target.value)}
          style={{ fontSize: "var(--fs-xs)", width: "auto" }} aria-label="Time" />
      </div>

      <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.5rem" }}>Tags <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.5rem" }}>
        {DEFAULT_TAGS.map((t) => (
          <button key={t} type="button" className={`chip${tags.includes(normalizeTag(t)) ? " selected" : ""}`} onClick={() => toggleTag(t)}>{t}</button>
        ))}
        {tags.filter((t) => !DEFAULT_TAGS.map(normalizeTag).includes(t)).map((t) => (
          <button key={t} type="button" className="chip selected" onClick={() => toggleTag(t)}>{t} <X size={11} /></button>
        ))}
      </div>
      <form className="row" style={{ gap: "0.4rem", marginBottom: "1.1rem" }} onSubmit={(e) => { e.preventDefault(); addCustom(); }}>
        <input placeholder="Add your own tag…" value={customTag} onChange={(e) => setCustomTag(e.target.value)} style={{ fontSize: "var(--fs-xs)" }} />
        <button type="submit" className="btn" style={{ fontSize: "var(--fs-xs)" }} disabled={!customTag.trim()}>Add</button>
      </form>

      <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.5rem" }}>Note <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></div>
      <textarea rows={3} placeholder="What happened today?" value={note} onChange={(e) => setNote(e.target.value)} style={{ marginBottom: "1rem" }} />

      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
        <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={score === null || saving}>{saving ? "Saving…" : isEditing ? "Update" : "Save entry"}</button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, children, onCancel, wide }: { title: string; children: React.ReactNode; onCancel: () => void; wide?: boolean }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(31,27,46,0.45)", display: "grid", placeItems: "center", zIndex: 50, padding: "1rem" }}>
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: wide ? 460 : 340, padding: "1.5rem", boxShadow: "var(--shadow-2)", borderRadius: "var(--radius-dialog)", maxHeight: "90vh", overflowY: "auto" }}>
        <div className="serif" style={{ fontWeight: 600, fontSize: "1.1rem", marginBottom: "1rem" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}
